import React, {CSSProperties} from 'react';
import {Box} from '@mui/material';
import {addCategoriesToAttributes, TDAForm} from "@dreebit-registry/vsm-microservice-components";
import InfrastructureSelect from "../InfrastructureSelect";
import InfrastructureBrowser from "../InfrastructureBrowser";
import NumberWithUnitInput, {
    NUMBER_WITH_UNIT_INPUT_TYPE,
    NUMBER_WITH_UNIT_ATTRIBUTE_INDICES,
    withNumberWithUnitInputType,
} from "../NumberWithUnitInput";
import NodeImageInput from "../NodeImageInput";
import { NODE_IMAGE_INPUT_TYPE } from "../../utils/nodeImage";
import { FormikHelpersRef } from '../../types';


interface Props {
    attributes: any,
    /** Return the mutation promise: TDAForm keeps the submit button loading
     *  until it settles, so a double click can't submit twice. */
    onSubmit?: (values: { [p: string]: any }) => void | Promise<unknown>,
    onValuesChange?: (values: { [p: string]: any }) => void,
    onReset?: () => void,
    onCancel?: () => void,
    submitButtonText?: string,
    cancelButtonText?: string,
    layout?: 'row' | 'column',
    useFilterLabel?: boolean,
    onChange?: (index: any, value: unknown) => void,
    initialFormValues?: { [p: string]: any },
    formStyle?: CSSProperties | undefined,
    searchParams?: any,
    onSearch?: (values: { query: string, fields: string[] }) => void,
    setFormikHelpersRef?: (ref: FormikHelpersRef) => void;
    enableReinitialize?: boolean
    customInputRenderers?: Record<string, any>
    /** Scopes installedBaseNodePicker attributes to the subtree below this node
     *  (no navigating up to the company list). Undefined = unrestricted. */
    nodePickerRootNodeId?: string
    /** 'browser' renders installedBaseNodePicker attributes as the inline
     *  InfrastructureBrowser instead of the dropdown. The inputType stays
     *  untouched, so the library's picker validation (required) still applies. */
    nodePickerVariant?: 'select' | 'browser'
    /** Renders every input disabled (read-only view of the form). */
    disableForm?: boolean
    /** Mirrors TDAForm.onDirtyChange: fires when the form's dirty state flips
     *  (unsaved changes vs. the current initial values). */
    onDirtyChange?: (dirty: boolean) => void
}

const InstalledBaseTDAForm = ({attributes, onSubmit, onValuesChange, onCancel, submitButtonText, initialFormValues, formStyle, setFormikHelpersRef, enableReinitialize, customInputRenderers, nodePickerRootNodeId, nodePickerVariant = 'select', disableForm, onDirtyChange}: Props) => {
    const handleSubmit = (inputValues: any) => {
        if (onSubmit) {
            return onSubmit(inputValues)
        }
    }

    const taggedAttributes = withNumberWithUnitInputType(attributes, NUMBER_WITH_UNIT_ATTRIBUTE_INDICES)
    let groupedAttributes = addCategoriesToAttributes(taggedAttributes, {'addressId': ['Address']})
    if (disableForm) {
        // TDAForm only disables its built-in inputs; custom renderers like
        // NumberWithUnitInput derive their state from attribute.disabled.
        groupedAttributes = groupedAttributes.map((attr: any) => ({...attr, disabled: true}))
    }

    const getAttributeLabel = (attribute: any) => {
        let label = attribute.name;

        if (attribute?.rules?.find((rule: any) => rule.name === "required")) {
            label = label + " *"
        }

        return label
    }

    // InfrastructureSelect/-Browser have no disabled prop — block interaction
    // from the outside when the whole form is read-only.
    const readOnlyGuard = (node: React.ReactNode) => disableForm
        ? <Box sx={{pointerEvents: 'none', opacity: 0.5}}>{node}</Box>
        : node;

    const renderNodeBrowser = ({attribute, value, error, touched, setFieldValue}: any) => readOnlyGuard(
        <InfrastructureBrowser
            label={getAttributeLabel(attribute)}
            value={value}
            onChange={(val) => setFieldValue(attribute.index, val)}
            error={Boolean(error && touched)}
            helperText={touched ? error : undefined}
            rootNodeId={nodePickerRootNodeId}
            // The scoped root stays pickable, as it is in the unscoped company list.
            rootSelectable={!!nodePickerRootNodeId}
            includeTrash={attribute._includeTrash}
        />
    );

    const mergedCustomInputRenderers = {
        // `_includeTrash` is a frontend-only marker set on synthesized destination
        // attributes; real (server) TDAs never carry it, so their pickers keep the
        // server default and stay able to offer the trash (ASSET_REMOVE relocation).
        installedBaseNodePicker: nodePickerVariant === 'browser' ? renderNodeBrowser : ({attribute, value, error, touched, setFieldValue}: any) => readOnlyGuard(
            <InfrastructureSelect
                label={getAttributeLabel(attribute)}
                value={value}
                onChange={(val) => setFieldValue(attribute.index, val)}
                error={Boolean(error && touched)}
                helperText={touched ? error : undefined}
                rootNodeId={nodePickerRootNodeId}
                includeTrash={attribute._includeTrash}
            />
        ),
        installedBaseNodeBrowser: renderNodeBrowser,
        [NUMBER_WITH_UNIT_INPUT_TYPE]: (props: any) => <NumberWithUnitInput {...props} />,
        // Node image (imageId TDA). Consumers re-tag the attribute via
        // withNodeImageInputType (needs the current thumbnail for the preview).
        // Rendered as JSX, not a component reference: TDAForm invokes renderers as
        // plain functions, so hooks must live in their own component.
        [NODE_IMAGE_INPUT_TYPE]: (props: any) => <NodeImageInput {...props} />,
        ...(customInputRenderers ?? {}),
    };

    return (
        // TDAForm (in @dreebit-registry/vsm-microservice-components) wraps each
        // attribute / custom-renderer slot in a Joy `<Grid>` without `xs={12}`,
        // so the slot is content-sized. With a wide custom renderer (e.g. the
        // embedded RegisterAssetModal), that content drives the slot's width
        // and the slot escapes its surrounding modal. Cap Joy/Mui Grid item
        // descendants here so renderers always respect the parent column.
        // Grid containers must stay unclamped: their spacing relies on
        // negative margins plus a width of 100% + spacing, and capping them
        // at 100% shifts rows left (e.g. the Cancel/Save row loses 8px on
        // the right and no longer aligns with the fields above).
        <Box sx={{
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            '& .MuiGrid-root:not(.MuiGrid-container)': {
                maxWidth: '100%',
                minWidth: 0,
            },
            // TDAForm's auxiliary buttons (e.g. the "set here" override on
            // inherited attributes) ignore disableForm — a read-only form must
            // not offer any interaction.
            ...(disableForm ? {
                '& form button': {pointerEvents: 'none', opacity: 0.5},
            } : {}),
        }}>
            <TDAForm
                formStyle={formStyle}
                onSubmit={onSubmit ? handleSubmit : undefined}
                onValuesChange={onValuesChange}
                onCancel={onCancel}
                submitButtonText={submitButtonText}
                attributes={groupedAttributes}
                initialFormValues={initialFormValues}
                setFormikHelpersRef={setFormikHelpersRef}
                enableReinitialize={enableReinitialize}
                customInputRenderers={mergedCustomInputRenderers}
                disableForm={disableForm}
                onDirtyChange={onDirtyChange}
            />
        </Box>
    );
}

export default InstalledBaseTDAForm;
